import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BlockchainStatusIndicator from '../../components/ui/BlockchainStatusIndicator';
import CriticalAlertsPanel from '../../components/ui/CriticalAlertsPanel';
import QuickActionLauncher from '../../components/ui/QuickActionLauncher';
import AuditFilters from './components/AuditFilters';
import AuditEntry from './components/AuditEntry';
import AuditAnalytics from './components/AuditAnalytics';
import BlockchainVerifier from './components/BlockchainVerifier';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AuditTrailViewer = () => {
  const [auditEntries, setAuditEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isVerifierOpen, setIsVerifierOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading audit trail data
    const loadAuditData = async () => {
      setIsLoading(true);
      
      // Mock audit trail entries
      const mockEntries = [
        {
          id: 1,
          action: "Batch Test Result Recorded",
          description: "HPLC purity test results for Batch QA-2024-1247 recorded with 99.2% purity level",
          user: "Dr. Sarah Chen",
          userRole: "qa_manager",
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
          activityType: "test_result",
          severity: "medium",
          verificationStatus: "verified",
          blockchainHash: "0x7f9fade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385",
          transactionHash: "0x8a1bfade2d1e68b8f77bc5fbe80gfade2d1e68b8f77bc5fbe8d3d3fc8c22b92496",
          blockNumber: 15847392,
          gasUsed: 21000,
          ipAddress: "192.168.1.45",
          sessionId: "sess_9f8e7d6c5b4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e",
          dataChanges: [
            { field: "purity_level", oldValue: "pending", newValue: "99.2%" },
            { field: "test_status", oldValue: "in_progress", newValue: "completed" }
          ]
        },
        {
          id: 2,
          action: "Equipment Calibration Completed",
          description: "HPLC System #3 calibration completed successfully with all parameters within acceptable range",
          user: "Mark Rodriguez",
          userRole: "lab_technician",
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
          activityType: "equipment_calibration",
          severity: "low",
          verificationStatus: "verified",
          blockchainHash: "0x6e8dcde0b1c46a6af55ab3dac68ecde0b1c46a6af55ab3dac6b1b1da7a00a80274",
          transactionHash: "0x7b0cfade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385",
          blockNumber: 15847388,
          gasUsed: 18500,
          ipAddress: "192.168.1.52",
          sessionId: "sess_8e7d6c5b4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d",
          dataChanges: [
            { field: "calibration_status", oldValue: "overdue", newValue: "completed" },
            { field: "next_calibration", oldValue: "2024-01-15", newValue: "2024-04-15" }
          ]
        },
        {
          id: 3,
          action: "Adverse Event Report Submitted",
          description: "New adverse event report AE-2024-0089 submitted for Product XYZ with moderate severity classification",
          user: "Dr. Emily Watson",
          userRole: "pharmacovigilance",
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
          activityType: "adverse_event",
          severity: "high",
          verificationStatus: "verified",
          blockchainHash: "0x5d7cbcd9a0b35a5ae44ab2cb957dbcd9a0b35a5ae44ab2cb95a0a0c969ff9f0163",
          transactionHash: "0x6a9bfade0b0c46a6af55ab3dac68fade0b0c46a6af55ab3dac6b1b1da7a00a80274",
          blockNumber: 15847385,
          gasUsed: 24000,
          ipAddress: "192.168.1.67",
          sessionId: "sess_7d6c5b4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c",
          dataChanges: [
            { field: "severity", oldValue: "unknown", newValue: "moderate" },
            { field: "status", oldValue: "draft", newValue: "submitted" }
          ]
        },
        {
          id: 4,
          action: "Regulatory Submission Approved",
          description: "FDA submission for Product ABC approved by regulatory team with all documentation verified",
          user: "James Thompson",
          userRole: "regulatory_officer",
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
          activityType: "regulatory_submission",
          severity: "medium",
          verificationStatus: "verified",
          blockchainHash: "0x4c6abbc8909245a9d33ab1ba846cbbc8909245a9d33ab1ba849f9f9b858ee8f052",
          transactionHash: "0x598afade9a0b35a5ae44ab2cb957fade9a0b35a5ae44ab2cb95a0a0c969ff9f0163",
          blockNumber: 15847380,
          gasUsed: 19800,
          ipAddress: "192.168.1.78",
          sessionId: "sess_6c5b4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c5b",
          dataChanges: [
            { field: "approval_status", oldValue: "pending_review", newValue: "approved" },
            { field: "submission_date", oldValue: "null", newValue: "2024-01-19" }
          ]
        },
        {
          id: 5,
          action: "Batch Quality Review Completed",
          description: "Quality review for Batch QA-2024-1245 completed with all parameters meeting specifications",
          user: "Dr. Sarah Chen",
          userRole: "qa_manager",
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
          activityType: "batch_approval",
          severity: "low",
          verificationStatus: "verified",
          blockchainHash: "0x3b59aab7808134a8c22ab0a9735baab7808134a8c22ab0a9738e8e8a747dd7e941",
          transactionHash: "0x487afade808134a8c22ab0a973fade808134a8c22ab0a9738e8e8a747dd7e941",
          blockNumber: 15847375,
          gasUsed: 16200,
          ipAddress: "192.168.1.45",
          sessionId: "sess_5b4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c5b4a",
          dataChanges: [
            { field: "review_status", oldValue: "in_review", newValue: "approved" },
            { field: "release_authorization", oldValue: "pending", newValue: "authorized" }
          ]
        },
        {
          id: 6,
          action: "User Access Modification",
          description: "Lab technician access permissions updated for enhanced security compliance",
          user: "System Administrator",
          userRole: "admin",
          timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000),
          activityType: "user_access",
          severity: "medium",
          verificationStatus: "verified",
          blockchainHash: "0x2a48aa96707023a7b11aa989624aa96707023a7b11aa989627d7d79636cc6d830",
          transactionHash: "0x376afade707023a7b11aa9896fade707023a7b11aa989627d7d79636cc6d830",
          blockNumber: 15847370,
          gasUsed: 22100,
          ipAddress: "192.168.1.10",
          sessionId: "sess_4a3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c5b4a3f",
          dataChanges: [
            { field: "access_level", oldValue: "standard", newValue: "restricted" },
            { field: "permissions", oldValue: "read_write", newValue: "read_only" }
          ]
        },
        {
          id: 7,
          action: "Document Version Update",
          description: "SOP-QC-001 Quality Control Procedures updated to version 3.2 with blockchain verification",
          user: "Dr. Michael Brown",
          userRole: "lab_director",
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
          activityType: "document_update",
          severity: "low",
          verificationStatus: "pending",
          blockchainHash: "0x1937a985606f12a6a00a9878513a985606f12a6a00a98785136c6c6852bb5c71f",
          transactionHash: "0x265afade606f12a6a00a9878fade606f12a6a00a98785136c6c6852bb5c71f",
          blockNumber: 15847365,
          gasUsed: 17800,
          ipAddress: "192.168.1.89",
          sessionId: "sess_3f2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c5b4a3f2e",
          dataChanges: [
            { field: "document_version", oldValue: "3.1", newValue: "3.2" },
            { field: "approval_status", oldValue: "approved", newValue: "pending_review" }
          ]
        },
        {
          id: 8,
          action: "Equipment Maintenance Logged",
          description: "Preventive maintenance completed on Spectrophotometer UV-2450 with all systems operational",
          user: "Alex Johnson",
          userRole: "lab_technician",
          timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000),
          activityType: "equipment_calibration",
          severity: "low",
          verificationStatus: "verified",
          blockchainHash: "0x0826a874505e01a5900a8677402a874505e01a5900a86774025b5b5741aa4b60e",
          transactionHash: "0x154afade505e01a5900a8677fade505e01a5900a86774025b5b5741aa4b60e",
          blockNumber: 15847360,
          gasUsed: 15600,
          ipAddress: "192.168.1.95",
          sessionId: "sess_2e1d0c9b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7d6c5b4a3f2e1d",
          dataChanges: [
            { field: "maintenance_status", oldValue: "due", newValue: "completed" },
            { field: "next_maintenance", oldValue: "2024-01-15", newValue: "2024-07-15" }
          ]
        }
      ];

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAuditEntries(mockEntries);
      setFilteredEntries(mockEntries);
      setIsLoading(false);
    };

    loadAuditData();
  }, []);

  const handleFiltersChange = (filters) => {
    let filtered = [...auditEntries];

    // Apply search filter
    if (filters?.searchQuery?.trim()) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(entry => 
        entry?.action?.toLowerCase()?.includes(query) ||
        entry?.description?.toLowerCase()?.includes(query) ||
        entry?.user?.toLowerCase()?.includes(query) ||
        entry?.blockchainHash?.toLowerCase()?.includes(query)
      );
    }

    // Apply role filter
    if (filters?.userRole !== 'all') {
      filtered = filtered?.filter(entry => entry?.userRole === filters?.userRole);
    }

    // Apply activity type filter
    if (filters?.activityType !== 'all') {
      filtered = filtered?.filter(entry => entry?.activityType === filters?.activityType);
    }

    // Apply verification status filter
    if (filters?.verificationStatus !== 'all') {
      filtered = filtered?.filter(entry => entry?.verificationStatus === filters?.verificationStatus);
    }

    // Apply date range filter
    const now = new Date();
    if (filters?.dateRange !== 'custom') {
      const daysMap = {
        'today': 1,
        'last_7_days': 7,
        'last_30_days': 30,
        'last_90_days': 90
      };
      
      const days = daysMap?.[filters?.dateRange];
      if (days) {
        const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
        filtered = filtered?.filter(entry => entry?.timestamp >= cutoffDate);
      }
    }

    setFilteredEntries(filtered);
    setCurrentPage(1);
  };

  const handleViewDetails = (entry) => {
    setSelectedEntry(entry);
    setIsVerifierOpen(true);
  };

  // Pagination
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredEntries?.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredEntries?.length / entriesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Audit Trail Viewer - IFET ChainLog</title>
        <meta name="description" content="Comprehensive blockchain transaction history and data integrity verification for pharmaceutical quality assurance processes" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Search" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Audit Trail Viewer</h1>
                <p className="text-muted-foreground">
                  Blockchain transaction history and data integrity verification
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <BlockchainStatusIndicator />
              <Button
                variant="outline"
                onClick={() => setIsAlertsOpen(true)}
                iconName="Bell"
                iconPosition="left"
              >
                Alerts
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="xl:col-span-3 space-y-6">
              {/* Filters */}
              <AuditFilters
                onFiltersChange={handleFiltersChange}
                totalRecords={filteredEntries?.length}
              />

              {/* Audit Entries */}
              <div className="bg-card border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h2 className="font-medium text-card-foreground">
                      Audit Trail Entries
                    </h2>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>
                        Showing {indexOfFirstEntry + 1}-{Math.min(indexOfLastEntry, filteredEntries?.length)} of {filteredEntries?.length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  {isLoading ? (
                    <div className="space-y-4">
                      {[...Array(5)]?.map((_, index) => (
                        <div key={index} className="animate-pulse">
                          <div className="h-20 bg-muted rounded-lg"></div>
                        </div>
                      ))}
                    </div>
                  ) : currentEntries?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-card-foreground mb-2">
                        No audit entries found
                      </h3>
                      <p className="text-muted-foreground">
                        Try adjusting your filters to see more results
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentEntries?.map((entry) => (
                        <AuditEntry
                          key={entry?.id}
                          entry={entry}
                          onViewDetails={handleViewDetails}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        iconName="ChevronLeft"
                        iconPosition="left"
                      >
                        Previous
                      </Button>
                      
                      <div className="flex items-center space-x-2">
                        {[...Array(Math.min(5, totalPages))]?.map((_, index) => {
                          const pageNumber = index + 1;
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`w-8 h-8 rounded-md text-sm font-medium transition-colors duration-150 ${
                                currentPage === pageNumber
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        iconName="ChevronRight"
                        iconPosition="right"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="xl:col-span-1">
              <AuditAnalytics />
            </div>
          </div>
        </div>
      </main>
      {/* Modals and Overlays */}
      <CriticalAlertsPanel
        isOpen={isAlertsOpen}
        onClose={() => setIsAlertsOpen(false)}
      />
      <BlockchainVerifier
        entry={selectedEntry}
        isOpen={isVerifierOpen}
        onClose={() => {
          setIsVerifierOpen(false);
          setSelectedEntry(null);
        }}
      />
      <QuickActionLauncher />
    </div>
  );
};

export default AuditTrailViewer;